import { Routes, Route, Navigate, useLocation } from 'react-router';
import { useAuthStore } from './stores/authStore';
import { useThemeStore } from './stores/themeStore';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useFamilyStore } from './stores/familyStore';
import { getPendingInviteToken } from './utils/pendingInviteToken';

// Pages
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const WaitlistPage = lazy(() => import('./pages/WaitlistPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const InvitePage = lazy(() => import('./pages/InvitePage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const PhotoLabPage = lazy(() => import('./pages/PhotoLabPage'));
const StoriesPage = lazy(() => import('./pages/StoriesPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const PublicFamilyPage = lazy(() => import('./pages/PublicFamilyPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const ExportPage = lazy(() => import('./pages/ExportPage'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const ConfirmEmailChangePage = lazy(() => import('./pages/ConfirmEmailChangePage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const ConsentPage = lazy(() => import('./pages/ConsentPage'));
const GamesPage = lazy(() => import('./pages/GamesPage'));
const QuizPage = lazy(() => import('./pages/QuizPage'));

const registrationMode = import.meta.env.VITE_REGISTRATION_MODE || 'waitlist';

function RegisterRoutePage() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const hasInviteContext =
    Boolean(searchParams.get('code')) ||
    Boolean(searchParams.get('invite')) ||
    Boolean(getPendingInviteToken());

  if (registrationMode === 'open' || hasInviteContext) {
    return <RegisterPage />;
  }

  return <WaitlistPage />;
}

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function FamilyRoute({ children }) {
  const {
    currentFamily,
    currentFamilyId,
    families,
    fetchFamilies,
    selectFamily,
  } = useFamilyStore();
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const bootstrapFamily = async () => {
      if (currentFamily) {
        if (!cancelled) setIsBootstrapping(false);
        return;
      }

      try {
        let availableFamilies = Array.isArray(families) ? families : [];
        if (availableFamilies.length === 0) {
          availableFamilies = await fetchFamilies();
        }

        let targetFamilyId = currentFamilyId;
        if (targetFamilyId && !availableFamilies.some((family) => family.id === targetFamilyId)) {
          targetFamilyId = null;
        }

        if (!targetFamilyId && availableFamilies.length > 0) {
          targetFamilyId = availableFamilies[0].id;
        }

        if (targetFamilyId) {
          await selectFamily(targetFamilyId);
        }
      } catch (error) {
        console.error('Failed to bootstrap current family:', error);
      } finally {
        if (!cancelled) {
          setIsBootstrapping(false);
        }
      }
    };

    bootstrapFamily();

    return () => {
      cancelled = true;
    };
  }, [currentFamily, currentFamilyId, families, fetchFamilies, selectFamily]);

  if (isBootstrapping) {
    return <RouteLoading />;
  }

  if (!currentFamily) {
    return <Navigate to="/app" replace />;
  }

  return children;
}

// Guest Route wrapper (redirect if already logged in)
function GuestRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  
  if (isLoading) {
    return null;
  }

  if (isAuthenticated) {
    return <Navigate to="/app" replace />;
  }

  return children;
}

// Home page - shows landing for guests, redirects to app for authenticated
function HomePage() {
  const { isAuthenticated, isLoading } = useAuthStore();
  
  if (isLoading) {
    return null;
  }

  if (isAuthenticated) {
    return <Navigate to="/app" replace />;
  }
  
  return <LandingPage />;
}

function RouteLoading() {
  return <div className="min-h-screen bg-[#f8f5ed]" />;
}

export default function App() {
  const { isDarkMode } = useThemeStore();
  const { initializeAuth } = useAuthStore();

  useEffect(() => {
    // Apply theme to html element to ensure all portals and modals inherit it
    if (isDarkMode) {
      document.documentElement.setAttribute('data-theme', 'dark');
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  return (
    <Suspense fallback={<RouteLoading />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<HomePage />} />

        {/* Guest-only routes */}
        <Route path="/login" element={
          <GuestRoute><LoginPage /></GuestRoute>
        } />
        <Route path="/register" element={
          <GuestRoute><RegisterRoutePage /></GuestRoute>
        } />
        <Route path="/waitlist" element={
          <GuestRoute><WaitlistPage /></GuestRoute>
        } />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/confirm-email-change" element={<ConfirmEmailChangePage />} />
        <Route path="/forgot-password" element={
          <GuestRoute><ForgotPasswordPage /></GuestRoute>
        } />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="/join/:token" element={<InvitePage />} />
        <Route path="/family/:slug" element={<PublicFamilyPage />} />

        {/* Protected routes */}
        <Route path="/app" element={
          <ProtectedRoute><DashboardPage /></ProtectedRoute>
        } />
        <Route path="/admin" element={<Navigate to="/features" replace />} />
        <Route path="/settings" element={
          <ProtectedRoute><FamilyRoute><SettingsPage /></FamilyRoute></ProtectedRoute>
        } />
        <Route path="/gallery" element={
          <ProtectedRoute><FamilyRoute><GalleryPage /></FamilyRoute></ProtectedRoute>
        } />
        <Route path="/photo-lab" element={
          <ProtectedRoute><FamilyRoute><PhotoLabPage /></FamilyRoute></ProtectedRoute>
        } />
        <Route path="/stories" element={
          <ProtectedRoute><FamilyRoute><StoriesPage /></FamilyRoute></ProtectedRoute>
        } />
        <Route path="/events" element={
          <ProtectedRoute><FamilyRoute><EventsPage /></FamilyRoute></ProtectedRoute>
        } />
        <Route path="/calendar" element={
          <ProtectedRoute><FamilyRoute><CalendarPage /></FamilyRoute></ProtectedRoute>
        } />
        <Route path="/account" element={
          <ProtectedRoute><AccountPage /></ProtectedRoute>
        } />
        <Route path="/export" element={
          <ProtectedRoute><FamilyRoute><ExportPage /></FamilyRoute></ProtectedRoute>
        } />
        <Route path="/features" element={
          <ProtectedRoute><FeaturesPage /></ProtectedRoute>
        } />

        <Route path="/app/games" element={
          <ProtectedRoute><FamilyRoute><GamesPage /></FamilyRoute></ProtectedRoute>
        } />
        <Route path="/app/quiz/:quizId" element={
          <ProtectedRoute><QuizPage /></ProtectedRoute>
        } />

        {/* Legal pages - public */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/consent" element={<ConsentPage />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
